import {
  GenerativeCatalog,
  GenerativeCatalogAsset,
  GenerativeCatalogColourStory,
  GenerativeCatalogPatternTrend,
  GenerativeDenimCatalog,
  GenerativeDenimTile,
  denimTilePreviewPath,
} from "./shared/generativeCatalog";
import {
  DENIM_MATERIALS,
  DENIM_WASHES,
  denimAssetApiPath,
} from "../../constants/imageGeneratorDictionaries";
import {
  CollectionBuildDenimTile,
  CollectionBuildResultDetails,
  ColorPaletteOption,
  PatternTrendChoice,
  PreferenceGroup,
  PreferenceOption,
  patternTrendLabel,
  snapshotCollectionBuildDetails,
  trimmedPantoneField,
} from "./collectionBuildConstants";
import type { CollectionBuildReportSlide } from "./CollectionBuildReportSlider";

export const colourStoriesToPalettes = (
  stories: GenerativeCatalogColourStory[]
): ColorPaletteOption[] =>
  stories.map(story => ({
    id: story.id,
    label: story.title,
    colors: story.palette.map(swatch => swatch.hex),
    swatches: story.palette.map(swatch => ({
      hex: swatch.hex,
      tier: swatch.tier,
      ...trimmedPantoneField(swatch.pantone),
    })),
  }));

const pushUniqueReportSlide = (
  out: CollectionBuildReportSlide[],
  seen: Set<string>,
  slide: CollectionBuildReportSlide
) => {
  const url = String(slide.url || "").trim();
  const id = String(slide.id || "").trim() || url;
  if (!url || !id || seen.has(id) || seen.has(url)) {
    return;
  }
  seen.add(id);
  seen.add(url);
  out.push({ id, name: slide.name || id, url });
};

/**
 * Editorial report pages from CB `report-pages-catalog` (`reports.slider_image_urls`).
 * Never use pattern-tile `assets` or surface-title fallbacks.
 */
export const reportSlidesFromPages = (
  pages:
    | Array<{ id?: string; name?: string; url?: string } | null | undefined>
    | null
    | undefined
): CollectionBuildReportSlide[] => {
  if (!Array.isArray(pages) || !pages.length) {
    return [];
  }
  const out: CollectionBuildReportSlide[] = [];
  const seen = new Set<string>();
  pages.forEach((page, index) => {
    if (!page || typeof page !== "object") {
      return;
    }
    const url = String(page.url || "").trim();
    if (!url) {
      return;
    }
    pushUniqueReportSlide(out, seen, {
      id: String(page.id || `page-${index + 1}`),
      name: String(page.name || `Page ${index + 1}`),
      url,
    });
  });
  return out;
};

/** Macro / capsule View report — colour + pattern deck slider pages. */
export const reportSlidesForMacroTheme = (
  pages:
    | Array<{ id?: string; name?: string; url?: string } | null | undefined>
    | null
    | undefined
): CollectionBuildReportSlide[] => reportSlidesFromPages(pages);

type ReportPageSource = {
  id?: string;
  name?: string;
  url?: string;
  sliderImageIndex?: number | null;
};

type ItemReportSource =
  | {
      id?: string;
      title?: string;
      name?: string;
      sliderImageUrl?: string | null;
      slider_image_url?: string | null;
      sliderImageIndex?: number | null;
      slider_image_index?: number | null;
      sliderRefs?: Array<{
        index?: number | null;
        url?: string | null;
      } | null> | null;
      pages?: Array<ReportPageSource | null | undefined>;
    }
  | null
  | undefined;

type ThemeReportSource =
  | {
      pages?: Array<ReportPageSource | null | undefined> | null;
      colourStories?: ItemReportSource[];
      patternTrends?: ItemReportSource[];
    }
  | null
  | undefined;

/**
 * Colour / micro View report — only the item's own slide URL.
 * Missing URL → empty (hide View report); never fall back to the parent deck.
 */
export const reportSlidesForItem = (
  item: ItemReportSource
): CollectionBuildReportSlide[] => {
  if (!item || typeof item !== "object") {
    return [];
  }
  const url = String(item.sliderImageUrl || item.slider_image_url || "").trim();
  if (!url) {
    return [];
  }
  const id = String(item.id || "").trim() || url;
  const name = String(item.title || item.name || "Report slide").trim();
  return [{ id, name: name || "Report slide", url }];
};

const reportPagesSignature = (
  pages: Array<ReportPageSource | null | undefined> | null | undefined
): string =>
  (pages || [])
    .map(page => String(page?.url || "").trim())
    .filter(Boolean)
    .join("|");

const itemSlideIndices = (
  items: ItemReportSource[] | undefined
): Set<number> => {
  const out = new Set<number>();
  (items || []).forEach(item => {
    if (!item || typeof item !== "object") {
      return;
    }
    const raw = item.sliderImageIndex ?? item.slider_image_index;
    if (raw == null || !Number.isFinite(Number(raw))) {
      return;
    }
    out.add(Number(raw));
  });
  return out;
};

const collectUniqueSlides = (
  groups: CollectionBuildReportSlide[][]
): CollectionBuildReportSlide[] => {
  const out: CollectionBuildReportSlide[] = [];
  const seen = new Set<string>();
  groups.forEach(group => {
    group.forEach(slide => pushUniqueReportSlide(out, seen, slide));
  });
  return out;
};

/**
 * Item slides: prefer a group's own pages when they are not the parent deck
 * (macros split colour vs pattern reports). Capsules share the full deck on
 * every item — skip that dump so callers can index-filter `theme.pages`.
 */
const slidesFromItemOwnPages = (
  items: ItemReportSource[] | undefined,
  themePages: Array<ReportPageSource | null | undefined> | null | undefined
): CollectionBuildReportSlide[] => {
  const parentSig = reportPagesSignature(themePages);
  const groups: CollectionBuildReportSlide[][] = [];
  (items || []).forEach(item => {
    if (!item || typeof item !== "object") {
      return;
    }
    const ownPages = item.pages;
    const ownSig = reportPagesSignature(ownPages);
    if (ownSig && ownSig !== parentSig) {
      groups.push(reportSlidesFromPages(ownPages));
    }
  });
  return collectUniqueSlides(groups);
};

const slidesFromItemUrls = (
  items: ItemReportSource[] | undefined
): CollectionBuildReportSlide[] =>
  collectUniqueSlides((items || []).map(item => reportSlidesForItem(item)));

const slidesFromThemePagesByIndex = (
  pages: Array<ReportPageSource | null | undefined> | null | undefined,
  keep: Set<number> | null,
  exclude: Set<number>
): CollectionBuildReportSlide[] => {
  const list = Array.isArray(pages) ? pages : [];
  const filtered = list.filter((page, index) => {
    if (!page) {
      return false;
    }
    const raw = page.sliderImageIndex;
    const n =
      raw != null && Number.isFinite(Number(raw)) ? Number(raw) : index + 1;
    if (keep && keep.size) {
      return keep.has(n);
    }
    return !exclude.has(n);
  });
  return reportSlidesFromPages(filtered);
};

/** Theme-level View report — colour stories only (no pattern-trend slides). */
export const reportSlidesForMacroThemeColour = (
  theme: ThemeReportSource
): CollectionBuildReportSlide[] => {
  if (!theme || typeof theme !== "object") {
    return [];
  }
  const fromOwnPages = slidesFromItemOwnPages(theme.colourStories, theme.pages);
  if (fromOwnPages.length) {
    return fromOwnPages;
  }
  const colourIdx = itemSlideIndices(theme.colourStories);
  const patternIdx = itemSlideIndices(theme.patternTrends);
  const fromParent = colourIdx.size
    ? slidesFromThemePagesByIndex(theme.pages, colourIdx, new Set())
    : patternIdx.size
      ? slidesFromThemePagesByIndex(theme.pages, null, patternIdx)
      : [];
  if (fromParent.length) {
    return fromParent;
  }
  return slidesFromItemUrls(theme.colourStories);
};

const collectItemPageIndices = (
  items: ItemReportSource[] | undefined
): Set<number> => {
  const out = itemSlideIndices(items);
  (items || []).forEach(item => {
    if (!item || typeof item !== "object") {
      return;
    }
    (item.pages || []).forEach(page => {
      const raw = page?.sliderImageIndex;
      if (raw == null || !Number.isFinite(Number(raw))) {
        return;
      }
      out.add(Number(raw));
    });
  });
  return out;
};

/**
 * Pattern-trend View report — COMPLETE theme-level pattern deck for the
 * selected macro/capsule (Ethnic Pattern Trend CTA). Prefers patternTrends
 * `pages` (editorial report-pages-catalog), not a single story `sliderImageUrl`.
 * Show CTA whenever a report deck has >= 1 page (not only multi-page decks).
 */
export const reportSlidesForMacroThemePattern = (
  theme: ThemeReportSource
): CollectionBuildReportSlide[] => {
  if (!theme || typeof theme !== "object") {
    return [];
  }
  const trends = (theme.patternTrends || []).filter(
    (item): item is NonNullable<ItemReportSource> =>
      Boolean(item) && typeof item === "object"
  );
  const fromOwnDistinct = slidesFromItemOwnPages(
    theme.patternTrends,
    theme.pages
  );
  if (fromOwnDistinct.length >= 1) {
    return fromOwnDistinct;
  }
  const fromTrendPages = collectUniqueSlides(
    trends.map(item => reportSlidesFromPages(item.pages))
  );
  const looksLikeStorySlides =
    trends.length > 0 &&
    fromTrendPages.length > 0 &&
    fromTrendPages.length <= trends.length &&
    trends.every(item => reportSlidesFromPages(item.pages).length <= 1);
  if (fromTrendPages.length >= 1 && !looksLikeStorySlides) {
    return fromTrendPages;
  }
  const patternIdx = collectItemPageIndices(theme.patternTrends);
  const colourIdx = itemSlideIndices(theme.colourStories);
  const fromParent = colourIdx.size
    ? slidesFromThemePagesByIndex(theme.pages, null, colourIdx)
    : patternIdx.size
      ? slidesFromThemePagesByIndex(theme.pages, patternIdx, new Set())
      : reportSlidesFromPages(theme.pages);
  if (fromParent.length >= 1) {
    return fromParent;
  }
  return fromTrendPages.length >= 1 ? fromTrendPages : [];
};

const patternItemPagesAreSharedDump = (
  theme: ThemeReportSource,
  item: NonNullable<ItemReportSource>,
  microId: string
): boolean => {
  const ownSig = reportPagesSignature(item.pages);
  if (!ownSig) {
    return true;
  }
  const parentSig = reportPagesSignature(theme?.pages);
  if (ownSig === parentSig) {
    return true;
  }
  return (theme?.patternTrends || []).some(other => {
    if (!other || typeof other !== "object") {
      return false;
    }
    if (String(other.id || "").trim() === microId) {
      return false;
    }
    return reportPagesSignature(other.pages) === ownSig;
  });
};

const storySlideUrl = (item: NonNullable<ItemReportSource>): string =>
  String(item.sliderImageUrl || item.slider_image_url || "").trim();

const rejectSingleStoryDeck = (
  slides: CollectionBuildReportSlide[],
  item: NonNullable<ItemReportSource>
): CollectionBuildReportSlide[] => {
  if (slides.length > 1) {
    return slides;
  }
  if (!slides.length) {
    return [];
  }
  const story = storySlideUrl(item);
  if (story && slides[0]?.url === story) {
    return [];
  }
  return slides;
};

const slidesMatchingMicroLabel = (
  pages: Array<ReportPageSource | null | undefined> | null | undefined,
  label: string
): CollectionBuildReportSlide[] => {
  const needle = String(label || "")
    .trim()
    .toLowerCase();
  if (!needle) {
    return [];
  }
  const filtered = (pages || []).filter(page => {
    if (!page) {
      return false;
    }
    const name = String(page.name || "")
      .trim()
      .toLowerCase();
    const id = String(page.id || "")
      .trim()
      .toLowerCase();
    const pageLabel = name.split(" ·")[0]?.trim() || "";
    return (
      name === needle ||
      name.startsWith(`${needle} ·`) ||
      name.startsWith(`${needle}:`) ||
      pageLabel === needle ||
      pageLabel.endsWith(needle) ||
      id.startsWith(`${needle}:`)
    );
  });
  return reportSlidesFromPages(filtered);
};

const slidesMatchingSliderRefs = (
  pages: Array<ReportPageSource | null | undefined> | null | undefined,
  item: NonNullable<ItemReportSource>
): CollectionBuildReportSlide[] => {
  const refs = (item.sliderRefs || []).filter(
    (ref): ref is { index?: number | null; url?: string | null } =>
      Boolean(ref) && typeof ref === "object"
  );
  const urls = new Set(
    refs.map(ref => String(ref.url || "").trim()).filter(Boolean)
  );
  const indexes = new Set(
    refs.map(ref => Number(ref.index)).filter(index => Number.isFinite(index))
  );
  if (urls.size >= 1) {
    const byUrl = reportSlidesFromPages(
      (pages || []).filter(page => urls.has(String(page?.url || "").trim()))
    );
    if (byUrl.length >= 1) {
      return byUrl;
    }
  }
  if (indexes.size >= 1) {
    const byIndex = slidesFromThemePagesByIndex(pages, indexes, new Set());
    if (byIndex.length >= 1) {
      return byIndex;
    }
  }
  return [];
};

/**
 * Western micro-theme View report — COMPLETE pattern report for one micro
 * (page names like "Pastoral · 1" from report-pages-catalog; sliderRefs when
 * present). Never returns a single story `slider_image_url`.
 * Report-pages catalog may omit `microThemes` (only patternTrends) — catalog
 * micro `pages` from image-generator/catalog are a valid complete deck.
 */
export const reportSlidesForMicroThemePattern = (
  theme: ThemeReportSource,
  microId: string,
  catalogTrend?: ItemReportSource
): CollectionBuildReportSlide[] => {
  const needle = String(microId || "").trim();
  if (!theme || typeof theme !== "object" || !needle) {
    return [];
  }
  const trends = (theme.patternTrends || []).filter(
    (item): item is NonNullable<ItemReportSource> =>
      Boolean(item) && typeof item === "object"
  );
  const item = trends.find(entry => String(entry.id || "").trim() === needle);
  const source: NonNullable<ItemReportSource> | undefined =
    item && catalogTrend && typeof catalogTrend === "object"
      ? {
          ...item,
          name: item.name || catalogTrend.name,
          title: item.title || catalogTrend.title,
          sliderRefs: catalogTrend.sliderRefs || item.sliderRefs,
          pages: reportPagesSignature(item.pages)
            ? item.pages
            : catalogTrend.pages,
        }
      : item ||
        (catalogTrend && typeof catalogTrend === "object"
          ? catalogTrend
          : undefined);
  if (!source) {
    return [];
  }

  const catalogPages =
    catalogTrend && typeof catalogTrend === "object"
      ? reportSlidesFromPages(catalogTrend.pages)
      : [];
  const sharedDump = item
    ? patternItemPagesAreSharedDump(theme, item, needle)
    : true;
  if (!sharedDump) {
    const own = rejectSingleStoryDeck(
      reportSlidesFromPages(source.pages),
      source
    );
    if (own.length >= 1) {
      return own;
    }
  }
  // Western micros live under catalog.microThemes; report-pages often lacks
  // that id on patternTrends — still show the micro's own report pages.
  if (catalogPages.length >= 1) {
    const fromCatalog = rejectSingleStoryDeck(catalogPages, source);
    if (fromCatalog.length >= 1) {
      return fromCatalog;
    }
  }

  const label = String(source.name || source.title || "").trim();
  const pagePool = reportPagesSignature(source.pages)
    ? source.pages
    : theme.pages;
  const fromLabel = rejectSingleStoryDeck(
    slidesMatchingMicroLabel(pagePool, label),
    source
  );
  if (fromLabel.length >= 1) {
    return fromLabel;
  }
  if (label) {
    const fromParentLabel = rejectSingleStoryDeck(
      slidesMatchingMicroLabel(theme.pages, label),
      source
    );
    if (fromParentLabel.length >= 1) {
      return fromParentLabel;
    }
  }

  const fromRefs = rejectSingleStoryDeck(
    slidesMatchingSliderRefs(pagePool, source),
    source
  );
  if (fromRefs.length >= 1) {
    return fromRefs;
  }

  if (trends.length <= 1) {
    return rejectSingleStoryDeck(
      reportSlidesForMacroThemePattern(theme),
      source
    );
  }
  return [];
};

/** Micro-theme story slide — item `slider_image_url` only (not the full report). */
export const reportSlidesForMicroTheme = (
  item: ItemReportSource
): CollectionBuildReportSlide[] => reportSlidesForItem(item);

/** Colour story View report — item `slider_image_url` only (no parent deck). */
export const reportSlidesForColourStory = (
  item: ItemReportSource
): CollectionBuildReportSlide[] => reportSlidesForItem(item);

export const flattenCatalogPatternTrends = (
  catalog: GenerativeCatalog | null
): GenerativeCatalogPatternTrend[] => {
  const seen = new Set<string>();
  return (catalog?.macros || []).flatMap(item =>
    item.patternTrends.filter(trend => {
      if (seen.has(trend.id)) {
        return false;
      }
      seen.add(trend.id);
      return true;
    })
  );
};

export const denimPaletteOptions = (
  catalog: GenerativeDenimCatalog
): ColorPaletteOption[] =>
  catalog.palettes.map(palette => ({
    id: palette.id,
    label: palette.label,
    colors: palette.hexes,
  }));

export const preferenceOptionsFromAssets = (
  assets: GenerativeCatalogAsset[],
  previews: Record<string, string>,
  labelForStem?: (stem: string) => string
): PreferenceOption[] =>
  assets
    .filter(asset => asset.available)
    .map(asset => ({
      id: asset.stem,
      imageUrl: previews[asset.stem] || "",
      label: String(labelForStem?.(asset.stem) || "").trim(),
    }));

/**
 * One Pattern Story (`pattern_trend`) as a preference group. Tile labels stay
 * empty — story names belong on pills, not under images.
 */
export const catalogPatternTrendPreferenceGroups = (
  trends: GenerativeCatalogPatternTrend[],
  previews: Record<string, string>,
  catalog: GenerativeCatalog | null = null
): PreferenceGroup[] =>
  (trends || [])
    .map(trend => ({
      id: String(trend.id || "").trim(),
      label: catalogPatternTrendLabel(trend, catalog),
      items: preferenceOptionsFromAssets(
        trend.assets || [],
        previews,
        () => ""
      ),
    }))
    .filter(group => group.id && group.items.length > 0);

export const catalogMacroMicroThemes = (
  macro:
    | {
        microThemes?: Array<{
          id?: string;
          name?: string | null;
          patternTrends?: GenerativeCatalogPatternTrend[];
          pages?: Array<{ id?: string; name?: string; url?: string }>;
        }>;
      }
    | null
    | undefined
) =>
  (macro?.microThemes || [])
    .map(micro => ({
      id: String(micro.id || "").trim(),
      name: String(micro.name || "").trim(),
      patternTrends: micro.patternTrends || [],
      pages: micro.pages || [],
    }))
    .filter(micro => micro.id);

export const catalogPatternStoriesForMicro = (
  macro: Parameters<typeof catalogMacroMicroThemes>[0],
  microId: string
): GenerativeCatalogPatternTrend[] => {
  const needle = String(microId || "").trim();
  if (!needle) {
    return [];
  }
  const micro = catalogMacroMicroThemes(macro).find(item => item.id === needle);
  return micro?.patternTrends || [];
};

export const hydrateMicroAndStorySelection = (
  macro: Parameters<typeof catalogMacroMicroThemes>[0] & {
    patternTrends?: GenerativeCatalogPatternTrend[];
  },
  persistedIds: string[]
): {
  microIds: string[];
  activeMicroId: string;
  storyIds: string[];
  /** First selected story (active / backward-compat singular). */
  storyId: string;
} => {
  const ids = persistedIds.map(id => String(id || "").trim()).filter(Boolean);
  const micros = catalogMacroMicroThemes(macro);
  const microIdSet = new Set(micros.map(item => item.id));
  const parentByStory = new Map<string, string>();
  const storyIdSet = new Set<string>();
  micros.forEach(micro => {
    (micro.patternTrends || []).forEach(trend => {
      const id = String(trend.id || "").trim();
      if (!id) {
        return;
      }
      storyIdSet.add(id);
      parentByStory.set(id, micro.id);
    });
  });
  (macro?.patternTrends || []).forEach(trend => {
    const id = String(trend.id || "").trim();
    if (id) {
      storyIdSet.add(id);
    }
  });
  const asMicros = ids.filter(id => microIdSet.has(id));
  const asStories = ids.filter(id => storyIdSet.has(id));
  if (asMicros.length) {
    return {
      microIds: asMicros,
      activeMicroId: asMicros[0] || "",
      storyIds: asStories,
      storyId: asStories[0] || "",
    };
  }
  if (asStories.length) {
    const parentIds = Array.from(
      new Set(asStories.map(id => parentByStory.get(id) || "").filter(Boolean))
    );
    return {
      microIds: parentIds,
      activeMicroId: parentIds[0] || "",
      storyIds: asStories,
      storyId: asStories[0] || "",
    };
  }
  return {
    microIds: ids,
    activeMicroId: ids[0] || "",
    storyIds: [],
    storyId: "",
  };
};

/**
 * Match BE `sampleMicroTargetCount`: ≤4 keep all; >4 random k ∈ {3, 4}.
 * Caps at `maxCap` (Joi limits). Used when FE must thin a multi-select before persist.
 */
const sampleMultiSelectTargetCount = (
  selectedCount: number,
  maxCap: number
): number => {
  if (selectedCount <= 0 || maxCap <= 0) {
    return 0;
  }
  if (selectedCount <= 4) {
    return Math.min(selectedCount, maxCap);
  }
  const k = 3 + Math.floor(Math.random() * 2);
  return Math.min(k, selectedCount, maxCap);
};

/** Unique ids; if over `maxCap`, random-sample a small count (BE micro style). */
export const sampleMultiSelectIds = (
  ids: string[],
  maxCap: number
): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const raw of ids) {
    const id = String(raw || "").trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    unique.push(id);
  }
  // Within Joi cap — send all (BE samples pattern trends further for prompt).
  if (unique.length <= maxCap) {
    return unique;
  }
  const target = sampleMultiSelectTargetCount(unique.length, maxCap);
  if (target >= unique.length) {
    return unique.slice(0, maxCap);
  }
  const copy = [...unique];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, target);
};

/** Unique live report assets from GET /image-generator/catalog (pattern-trend tiles). */
export const flattenCatalogSketchAssets = (
  catalog: GenerativeCatalog | null,
  macro?: GenerativeCatalog["macros"][number]
): GenerativeCatalogAsset[] => {
  const macros = macro ? [macro] : catalog?.macros || [];
  const seen = new Set<string>();
  return macros.flatMap(item =>
    item.patternTrends.flatMap(trend =>
      trend.assets.filter(asset => {
        if (!asset.available || !asset.url || seen.has(asset.stem)) {
          return false;
        }
        seen.add(asset.stem);
        return true;
      })
    )
  );
};

const catalogMacrosAndCapsules = (catalog: GenerativeCatalog | null) => [
  ...(catalog?.macros || []),
  ...(catalog?.capsules || []),
];

/** Pipe-delimited catalog ids → last segment title-cased (e.g. …|shakti → Shakti). */
export const humanizeCollectionThemeId = (value: string): string => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }
  if (!raw.includes("|")) {
    return raw;
  }
  const segment = raw.split("|").pop() || raw;
  return segment
    .split(/[-_]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

export const findCatalogMacroOrCapsule = (
  catalog: GenerativeCatalog | null,
  id: string
) => {
  const needle = String(id || "").trim();
  if (!needle) {
    return undefined;
  }
  return catalogMacrosAndCapsules(catalog).find(
    item => String(item.id) === needle
  );
};

export const catalogMacroThemeLabel = (
  catalog: GenerativeCatalog | null,
  id: string
): string => {
  const found = findCatalogMacroOrCapsule(catalog, id);
  if (found?.name) {
    return found.name;
  }
  return humanizeCollectionThemeId(id);
};

const isBareCatalogId = (value: string): boolean => /^\d+$/.test(value);

/** Live catalog micro-theme / pattern-trend caption (`name` or `title`). */
export const catalogPatternTrendName = (
  trend:
    | {
        name?: string | null;
        title?: string | null;
      }
    | null
    | undefined
): string => String(trend?.name || trend?.title || "").trim();

/**
 * Pattern Story / micro-theme display name (`pattern_trend` name or title).
 * Use as the group heading over preference tiles — never drop this in favor
 * of a tile stem as the only label for the whole group.
 */
export const catalogPatternTrendLabel = (
  trend:
    | {
        id?: string;
        name?: string | null;
        title?: string | null;
      }
    | null
    | undefined,
  catalog: GenerativeCatalog | null = null
): string => {
  const named = catalogPatternTrendName(trend);
  if (named) {
    return named;
  }
  const id = String(trend?.id || "").trim();
  if (!id) {
    return "";
  }
  return (
    catalogMicroThemeLabel(catalog, id) || humanizeCollectionThemeId(id) || id
  );
};

/**
 * Resolve a pattern-trend / micro-theme id to a display name.
 * Catalog hit → trend.name. Else humanize pipe/slug ids. Bare numeric ids are
 * kept only while catalog is missing (so a later enrich can resolve them);
 * once catalog is present and unmatched, return "" so Design Details never
 * shows a raw patternTrendId like "375".
 */
export const catalogMicroThemeLabel = (
  catalog: GenerativeCatalog | null,
  id: string
): string => {
  const needle = String(id || "").trim();
  if (!needle) {
    return "";
  }
  for (const macro of catalogMacrosAndCapsules(catalog)) {
    const micros = catalogMacroMicroThemes(macro);
    const microHit = micros.find(item => item.id === needle);
    if (microHit?.name) {
      return microHit.name;
    }
    const parent = micros.find(item =>
      (item.patternTrends || []).some(trend => String(trend.id) === needle)
    );
    if (parent?.name) {
      return parent.name;
    }
    const trend = macro.patternTrends.find(item => String(item.id) === needle);
    const named = catalogPatternTrendName(trend);
    if (named) {
      return named;
    }
  }
  if (!catalog) {
    // Defer bare ids until enrich; still humanize slug-shaped values now.
    return isBareCatalogId(needle) ? needle : humanizeCollectionThemeId(needle);
  }
  if (isBareCatalogId(needle)) {
    return "";
  }
  return humanizeCollectionThemeId(needle);
};

export const findCatalogAssetByStem = (
  catalog: GenerativeCatalog | null,
  stem: string
): GenerativeCatalogAsset | undefined => {
  const needle = String(stem || "").trim();
  if (!needle) {
    return undefined;
  }
  for (const macro of catalogMacrosAndCapsules(catalog)) {
    for (const trend of macro.patternTrends) {
      const asset = trend.assets.find(item => item.stem === needle);
      if (asset) {
        return asset;
      }
    }
  }
  return undefined;
};

/** Recommend path: first available catalog tile (BE may replace after assemble/poll). */
export const recommendedPreferenceStemsFromAssets = (
  assets: GenerativeCatalogAsset[]
): string[] => {
  const seen = new Set<string>();
  const available: string[] = [];
  const rest: string[] = [];
  (assets || []).forEach(asset => {
    const stem = String(asset?.stem || "").trim();
    if (!stem || seen.has(stem)) {
      return;
    }
    seen.add(stem);
    if (asset.available) {
      available.push(stem);
    } else {
      rest.push(stem);
    }
  });
  const pool = available.length ? available : rest;
  return pool.slice(0, 1);
};

/** Design-details preference tiles: prefer catalog asset URLs (auth-gated), not blob previews. */
export const preferenceOptionsForDetails = (
  stems: string[],
  catalog: GenerativeCatalog | null,
  assets?: GenerativeCatalogAsset[]
): PreferenceOption[] =>
  stems
    .map(stem => String(stem || "").trim())
    .filter(Boolean)
    .map(stem => {
      const fromList = (assets || []).find(item => item.stem === stem);
      const fromCatalog = findCatalogAssetByStem(catalog, stem);
      // Prefer an entry that actually has a url (list stubs can be url-less).
      const asset =
        (fromList?.url ? fromList : undefined) ||
        (fromCatalog?.url ? fromCatalog : undefined) ||
        fromList ||
        fromCatalog;
      return {
        id: stem,
        imageUrl: asset?.url || "",
        label: stem,
      };
    });

const resolvePatternTrendDisplay = (value: string): string => {
  const raw = String(value || "").trim();
  if (raw === "choose" || raw === "recommend") {
    return patternTrendLabel(raw as PatternTrendChoice);
  }
  if (raw.toLowerCase() === "none") {
    return "";
  }
  return raw;
};

const persistPatternTrendIds = (
  details: CollectionBuildResultDetails
): string[] => {
  const payload = details.persistPayload as
    | { patternTrendId?: string; patternTrendIds?: string[] }
    | undefined;
  const rawIds = payload?.patternTrendIds;
  const fromArray = Array.isArray(rawIds)
    ? rawIds.map(id => String(id || "").trim()).filter(Boolean)
    : [];
  if (fromArray.length) {
    return fromArray;
  }
  const single = String(payload?.patternTrendId || "").trim();
  return single ? [single] : [];
};

/**
 * Prefer catalog titles for micro-theme(s); try persistPayload.patternTrendIds
 * then the current display value. Never leave bare numeric ids once catalog
 * has been consulted. Multi-select joins labels with ", ".
 */
export const resolveMicroThemeDisplay = (
  details: CollectionBuildResultDetails,
  catalog: GenerativeCatalog | null
): string => {
  const labels = resolveMicroThemeLabels(details, catalog);
  return labels.join(", ");
};

/** All selected micro-theme display names (never only the first). */
export const resolveMicroThemeLabels = (
  details: CollectionBuildResultDetails,
  catalog: GenerativeCatalog | null
): string[] => {
  const current = String(details.microTheme || "").trim();
  const fromDetailsList = (details.microThemes || [])
    .map(item => String(item || "").trim())
    .filter(Boolean);
  const persistIds = persistPatternTrendIds(details);

  const resolveId = (id: string) => {
    if (!catalog) {
      if (isBareCatalogId(id)) {
        return id;
      }
      return humanizeCollectionThemeId(id) || id;
    }
    return catalogMicroThemeLabel(catalog, id) || "";
  };

  if (persistIds.length) {
    if (!catalog) {
      if (fromDetailsList.length >= persistIds.length) {
        return fromDetailsList;
      }
      if (current && !isBareCatalogId(current) && !current.includes("|")) {
        const parts = current
          .split(",")
          .map(part => part.trim())
          .filter(Boolean);
        if (parts.length >= persistIds.length) {
          return parts;
        }
      }
      return persistIds.map(resolveId).filter(Boolean);
    }
    const labels = persistIds.map(resolveId).filter(Boolean);
    if (labels.length) {
      return labels;
    }
  }

  if (fromDetailsList.length) {
    return fromDetailsList
      .map(part => {
        if (!catalog) {
          return isBareCatalogId(part)
            ? part
            : humanizeCollectionThemeId(part) || part;
        }
        return (
          catalogMicroThemeLabel(catalog, part) ||
          (isBareCatalogId(part) ? "" : humanizeCollectionThemeId(part) || part)
        );
      })
      .filter(Boolean);
  }

  const currentParts = current
    .split(",")
    .map(part => part.trim())
    .filter(Boolean);
  if (currentParts.length) {
    const labels = currentParts.map(part => {
      if (!catalog) {
        return isBareCatalogId(part)
          ? part
          : humanizeCollectionThemeId(part) || part;
      }
      return (
        catalogMicroThemeLabel(catalog, part) ||
        (isBareCatalogId(part) ? "" : humanizeCollectionThemeId(part) || part)
      );
    });
    return labels.filter(Boolean);
  }

  return [];
};

export type DenimWashLookup = {
  id: string;
  label: string;
  url?: string;
};

export type DenimCatalogForDetails = {
  materials?: DenimWashLookup[] | null;
  washes?: DenimWashLookup[] | null;
};

const humanizeDenimSlug = (raw: string): string =>
  raw
    .split("-")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

/** Resolve denim wash id → display label; keep a non-id label as-is. */
export const resolveDenimWashDisplay = (
  washOrId: string,
  washes?: DenimWashLookup[] | null
): string => {
  const raw = String(washOrId || "").trim();
  if (!raw) {
    return "";
  }
  const lookup = [...(washes || []), ...DENIM_WASHES];
  const match = lookup.find(item => item.id === raw || item.label === raw);
  if (match?.label) {
    return match.label;
  }
  // Slug id without catalog yet — title-case hyphens (not raw id).
  if (/^[a-z0-9]+(?:-[a-z0-9]+)+$/i.test(raw)) {
    return humanizeDenimSlug(raw);
  }
  return raw;
};

/**
 * Unique denim material/wash tiles from Look Details styles (order preserved).
 * Used by Design Details so every style selection is visible on the left rail.
 */
export const denimTilesFromStyleSelections = (
  styles: Array<{
    denimMaterialId?: string | null;
    denimWashId?: string | null;
  }>,
  materialCatalog?: DenimWashLookup[] | GenerativeDenimTile[] | null,
  washCatalog?: DenimWashLookup[] | GenerativeDenimTile[] | null
): {
  denimMaterialTiles: CollectionBuildDenimTile[];
  denimWashTiles: CollectionBuildDenimTile[];
  denimMaterialTile?: CollectionBuildDenimTile;
  denimWashTile?: CollectionBuildDenimTile;
} => {
  const materialTiles: CollectionBuildDenimTile[] = [];
  const washTiles: CollectionBuildDenimTile[] = [];
  const seenMaterials = new Set<string>();
  const seenWashes = new Set<string>();
  styles.forEach(style => {
    const materialId = String(style.denimMaterialId || "").trim();
    if (materialId && !seenMaterials.has(materialId)) {
      const tile = resolveDenimCatalogTile(
        "materials",
        materialId,
        materialCatalog
      );
      if (tile) {
        seenMaterials.add(materialId);
        materialTiles.push(tile);
      }
    }
    const washId = String(style.denimWashId || "").trim();
    if (washId && !seenWashes.has(washId)) {
      const tile = resolveDenimCatalogTile("washes", washId, washCatalog);
      if (tile) {
        seenWashes.add(washId);
        washTiles.push(tile);
      }
    }
  });
  return {
    denimMaterialTiles: materialTiles,
    denimWashTiles: washTiles,
    denimMaterialTile: materialTiles[0],
    denimWashTile: washTiles[0],
  };
};

/** Form-picker asset URL + label for a denim material or wash id. */
export const resolveDenimCatalogTile = (
  kind: "materials" | "washes",
  idOrLabel: string,
  catalogTiles?: DenimWashLookup[] | GenerativeDenimTile[] | null
): CollectionBuildDenimTile | null => {
  const raw = String(idOrLabel || "").trim();
  if (!raw) {
    return null;
  }
  const fallback: DenimWashLookup[] =
    kind === "materials"
      ? DENIM_MATERIALS.map(item => ({ id: item.id, label: item.label }))
      : DENIM_WASHES.map(item => ({ id: item.id, label: item.label }));
  const lookup = [...(catalogTiles || []), ...fallback];
  const match = lookup.find(item => item.id === raw || item.label === raw);
  const id = match?.id || raw;
  const label =
    match?.label ||
    (/^[a-z0-9]+(?:-[a-z0-9]+)+$/i.test(raw) ? humanizeDenimSlug(raw) : raw);
  return {
    id,
    label,
    imageUrl: denimTilePreviewPath({
      id,
      kind,
      url: match?.url || denimAssetApiPath(kind, id),
    }),
  };
};

const persistDenimField = (
  details: CollectionBuildResultDetails,
  key: "denimWashId" | "denimMaterialId"
): string =>
  String(
    (
      details.persistPayload as
        | { denimWashId?: string; denimMaterialId?: string }
        | undefined
    )?.[key] || ""
  ).trim();

const persistDenimWashId = (details: CollectionBuildResultDetails): string =>
  persistDenimField(details, "denimWashId");

const persistDenimMaterialId = (
  details: CollectionBuildResultDetails
): string => persistDenimField(details, "denimMaterialId");

const sameDenimTile = (
  a?: CollectionBuildDenimTile,
  b?: CollectionBuildDenimTile
) =>
  (a?.id || "") === (b?.id || "") &&
  (a?.label || "") === (b?.label || "") &&
  (a?.imageUrl || "") === (b?.imageUrl || "");

const persistSelectedAssetStems = (
  details: CollectionBuildResultDetails
): string[] => {
  const stems = (
    details.persistPayload as { selectedAssetStems?: unknown } | undefined
  )?.selectedAssetStems;
  if (!Array.isArray(stems)) {
    return [];
  }
  return stems.map(stem => String(stem || "").trim()).filter(Boolean);
};

/**
 * Map raw session ids / empty preference urls onto catalog display labels + asset urls.
 * Safe to call repeatedly; already-resolved labels are left alone when catalog has no id match.
 * Pass denim catalog tiles so Denim Design Details can show material/wash images.
 */
export const enrichCollectionBuildDetails = (
  details: CollectionBuildResultDetails,
  catalog: GenerativeCatalog | null,
  denimCatalog?: DenimCatalogForDetails | null
): CollectionBuildResultDetails => {
  const isDenim = /denim/i.test(details.startingDirection || "");
  const denimWashes = denimCatalog?.washes;
  const denimMaterials = denimCatalog?.materials;
  const macroFromId = findCatalogMacroOrCapsule(catalog, details.macroTheme);
  const macroTheme = isDenim
    ? ""
    : macroFromId?.name
      ? macroFromId.name
      : details.macroTheme.includes("|")
        ? humanizeCollectionThemeId(details.macroTheme)
        : details.macroTheme;

  const microThemes = isDenim ? [] : resolveMicroThemeLabels(details, catalog);
  const microTheme = microThemes.join(", ");

  const patternTrend = isDenim
    ? ""
    : resolvePatternTrendDisplay(details.patternTrend);

  const washId =
    persistDenimWashId(details) ||
    details.denimWashTile?.id ||
    details.wash ||
    "";
  const wash = resolveDenimWashDisplay(washId, denimWashes);
  const denimWashTile = isDenim
    ? resolveDenimCatalogTile("washes", washId, denimWashes) ||
      details.denimWashTile
    : undefined;
  const denimMaterialTile = isDenim
    ? resolveDenimCatalogTile(
        "materials",
        persistDenimMaterialId(details) || details.denimMaterialTile?.id || "",
        denimMaterials
      ) || details.denimMaterialTile
    : undefined;

  const preferenceSource = isDenim
    ? []
    : details.preferences.length > 0
      ? details.preferences
      : preferenceOptionsForDetails(
          persistSelectedAssetStems(details),
          catalog
        );

  const preferences = preferenceSource.map(item => {
    if (item.imageUrl && !item.imageUrl.startsWith("blob:")) {
      return item;
    }
    const asset = findCatalogAssetByStem(catalog, item.id);
    return {
      ...item,
      imageUrl:
        asset?.url || (item.imageUrl.startsWith("blob:") ? "" : item.imageUrl),
      label: item.label || item.id,
    };
  });

  if (
    macroTheme === details.macroTheme &&
    microTheme === details.microTheme &&
    (details.microThemes || []).join("\0") === microThemes.join("\0") &&
    patternTrend === details.patternTrend &&
    wash === details.wash &&
    sameDenimTile(denimMaterialTile, details.denimMaterialTile) &&
    sameDenimTile(denimWashTile, details.denimWashTile) &&
    preferences.length === details.preferences.length &&
    preferences.every(
      (item, index) =>
        item.imageUrl === details.preferences[index]?.imageUrl &&
        item.label === details.preferences[index]?.label
    )
  ) {
    return details;
  }

  return snapshotCollectionBuildDetails({
    ...details,
    macroTheme,
    microTheme,
    microThemes,
    patternTrend,
    wash,
    denimMaterialTile,
    denimWashTile,
    preferences,
  });
};
